import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import styles from './AdminPendingEmails.module.css';

const SEND_PENDING_EMAILS_URL =
  'https://us-central1-cheriecoiffer-react.cloudfunctions.net/sendPendingEmails';

function getEmailPreviewHtml(name, profileId) {
  const displayName = (name || '').trim() || 'there';
  const baseUrl = 'https://cheriecoiffer.com';
  const activateUrl = profileId ? `${baseUrl}/activate/${profileId}` : baseUrl;
  return `
    <div style="font-family: sans-serif; max-width: 560px; margin: 0 auto; padding: 20px; background: #fff; border-radius: 12px;">
      <p style="color: #333; font-size: 16px; line-height: 1.6; margin-bottom: 20px;">Hi <strong>${displayName}</strong>,</p>
      <p style="color: #333; font-size: 16px; line-height: 1.6;"><strong>You created a profile on <span style="color: #6b4199;">Chérie Coiffer</span>, but it is not visible to clients yet.</strong></p>
      <p style="color: #333; font-size: 16px; line-height: 1.6; margin-top: 20px;">Activate your profile to appear in city searches:</p>
      <p style="color: #666; font-size: 14px; margin-bottom: 16px;"><a href="${activateUrl}" style="color: #5e2b75;">${activateUrl}</a></p>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${activateUrl}" style="display: inline-block; background: linear-gradient(135deg, #6b4199, #a35bd6); color: white !important; padding: 14px 28px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px;">Activate my profile</a>
        <p style="color: #888; font-size: 12px; margin-top: 16px;">You are receiving this email because you created a profile on Chérie Coiffer.</p>
      </div>
      <div style="background: #e8f5e9; border: 1px solid #a5d6a7; border-radius: 8px; padding: 16px; margin: 24px 0;">
        <p style="color: #2e7d32; font-size: 16px; line-height: 1.6; margin: 0;">Once activated, clients will be able to find your profile and contact you directly.</p>
      </div>
      <p style="color: #333; font-size: 16px; line-height: 1.6;">If you need help, just reply to this email.</p>
      <p style="color: #333; font-size: 16px; margin-top: 24px;">— Ndeleila, Founder of Chérie Coiffer</p>
    </div>
  `;
}

function AdminPendingEmails() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewProfile, setPreviewProfile] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!currentUser || currentUser.displayName?.toLowerCase() !== 'admin') return;

    const fetchProfiles = async () => {
      setLoading(true);
      setError('');
      try {
        const snapshot = await getDocs(
          query(
            collection(db, 'hairstylists'),
            where('paymentStatus', '==', 'pending')
          )
        );
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        list.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setProfiles(list);
      } catch (err) {
        setError(`Erreur : ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [currentUser]);

  if (!currentUser || currentUser.displayName?.toLowerCase() !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <h1>Accès refusé</h1>
          <p>Vous devez être administrateur pour accéder à cette page.</p>
          <button type="button" onClick={() => navigate('/')} className={styles.backButton}>
            Retour à l&apos;accueil
          </button>
        </div>
      </div>
    );
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(profiles.map((p) => p.id)));
  };

  const selectNone = () => {
    setSelectedIds(new Set());
  };

  const selectPendingOnly = () => {
    setSelectedIds(new Set(profiles.filter((p) => p.paymentStatus === 'pending').map((p) => p.id)));
  };

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      setError('Sélectionnez au moins un profil.');
      return;
    }
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(SEND_PENDING_EMAILS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ profileIds: Array.from(selectedIds) }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(`${data.sent} email(s) envoyé(s). ${data.failed > 0 ? `${data.failed} échec(s).` : ''}`);
        setSelectedIds(new Set());
      } else {
        setError(data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setError(`Erreur : ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>Profils coiffeurs</h1>
        <p className={styles.subtitle}>
          Liste de tous les profils avec statut et type. Prévisualisez et envoyez l&apos;email
          &quot;pending&quot; personnalisé.
        </p>

        {error && <div className={styles.errorMessage}>{error}</div>}
        {success && <div className={styles.successMessage}>{success}</div>}

        {loading ? (
          <div className={styles.loading}>Chargement des profils...</div>
        ) : (
          <>
            <div className={styles.stats}>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Profils en attente :</span>
                <span className={styles.statValue}>{profiles.length}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Sélectionnés :</span>
                <span className={styles.statValue}>{selectedIds.size}</span>
              </div>
            </div>

            <div className={styles.toolbar}>
              <div className={styles.selectButtons}>
                <button type="button" onClick={selectAll} className={styles.toolbarBtn}>
                  Tout sélectionner
                </button>
                <button type="button" onClick={selectNone} className={styles.toolbarBtn}>
                  Tout désélectionner
                </button>
                <button type="button" onClick={selectPendingOnly} className={styles.toolbarBtn}>
                  Sélectionner les pending
                </button>
              </div>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || selectedIds.size === 0}
                className={styles.sendButton}
              >
                {sending ? 'Envoi en cours...' : `Envoyer l'email (${selectedIds.size})`}
              </button>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.checkCol} />
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Statut paiement</th>
                    <th>Compte</th>
                    <th>Ville</th>
                    <th>Pays</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.length === 0 ? (
                    <tr>
                      <td colSpan="9" className={styles.noData}>
                        Aucun profil trouvé
                      </td>
                    </tr>
                  ) : (
                    profiles.map((p) => (
                      <tr
                        key={p.id}
                        className={
                          p.paymentStatus === 'pending' ? styles.rowPending : styles.rowOk
                        }
                      >
                        <td className={styles.checkCol}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={() => toggleSelect(p.id)}
                            className={styles.checkbox}
                          />
                        </td>
                        <td className={styles.nameCell}>
                          <button
                            type="button"
                            className={styles.profileLink}
                            onClick={() => navigate(`/provider/hair/${p.id}`)}
                          >
                            {p.name || '-'}
                          </button>
                        </td>
                        <td className={styles.emailCell}>{p.userEmail || '-'}</td>
                        <td className={styles.typeCell}>{p.type || '-'}</td>
                        <td>
                          <span
                            className={`${styles.badge} ${
                              p.paymentStatus === 'pending'
                                ? styles.badgePending
                                : p.paymentStatus === 'ok'
                                  ? styles.badgeOk
                                  : styles.badgeOther
                            }`}
                          >
                            {p.paymentStatus || '-'}
                          </span>
                        </td>
                        <td className={styles.typeCell}>{p.accountType || '-'}</td>
                        <td className={styles.typeCell}>{p.mainCity || p.city || '-'}</td>
                        <td className={styles.typeCell}>{p.country || '-'}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => setPreviewProfile(p)}
                            className={styles.previewBtn}
                          >
                            Prévisualiser
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={() => navigate('/')}
              className={styles.backButton}
            >
              Retour à l&apos;accueil
            </button>
          </>
        )}
      </div>

      {previewProfile && (
        <div className={styles.modalOverlay} onClick={() => setPreviewProfile(null)}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3>Prévisualisation pour {previewProfile.name || 'there'}</h3>
              <button
                type="button"
                onClick={() => setPreviewProfile(null)}
                className={styles.closeBtn}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <div
                className={styles.emailPreview}
                dangerouslySetInnerHTML={{
                  __html: getEmailPreviewHtml(previewProfile.name, previewProfile.id),
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPendingEmails;
